import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';


import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';
import { adminApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Admin = () => {
  const { t } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  // Appointments state
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  
  // Availability state
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityDialog, setAvailabilityDialog] = useState(false);
  const [availabilityData, setAvailabilityData] = useState({
    date: dayjs(),
    startTime: dayjs().hour(9).minute(0),
    endTime: dayjs().hour(17).minute(0),
    duration: 60,
    isRecurring: false,
    recurringDays: [],
    recurringEndDate: dayjs().add(3, 'month'),
  });
  
  // Edit availability state
  const [editAvailabilityDialog, setEditAvailabilityDialog] = useState(false);
  const [slotToEdit, setSlotToEdit] = useState(null);
  const [editSlotData, setEditSlotData] = useState({
    newTime: '',
    newEndTime: '',
    isAvailable: true,
    updateRecurringSeries: false
  });
  
  // Admin appointment creation state
  const [adminAppointmentDialog, setAdminAppointmentDialog] = useState(false);
  const [adminAppointmentData, setAdminAppointmentData] = useState({
    date: dayjs(),
    time: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    discountCode: ''
  });
  
  // Cancel appointment state
  const [cancelDialog, setCancelDialog] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [notifyClient, setNotifyClient] = useState(true);

  // Weekly availability dialog state
  const [weeklyAvailabilityDialog, setWeeklyAvailabilityDialog] = useState(false);
  const [weeklyAvailability, setWeeklyAvailability] = useState({});
  const [weeklyAvailabilityLoading, setWeeklyAvailabilityLoading] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(dayjs().startOf('week'));

  // Daily appointments timeline state
  const [dailyAppointments, setDailyAppointments] = useState([]);
  const [dailyAppointmentsLoading, setDailyAppointmentsLoading] = useState(false);

  useEffect(() => {
    checkAuthStatus();
    
    // Debug: Log all environment variables
    console.log('=== Environment Variables Debug ===');
    console.log('REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
    console.log('REACT_APP_GOOGLE_CLIENT_ID:', process.env.REACT_APP_GOOGLE_CLIENT_ID);
    console.log('REACT_APP_RECAPTCHA_SITE_KEY:', process.env.REACT_APP_RECAPTCHA_SITE_KEY);
    console.log('NODE_ENV:', process.env.NODE_ENV);
    console.log('==================================');
    
    // Initialize Google Sign-In after a short delay to ensure DOM is ready
    setTimeout(() => {
      initializeGoogleSignIn();
    }, 500);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadAppointments();
      if (activeTab === 1) {
        loadAvailabilitySlots();
      }
    }
  }, [isAuthenticated, activeTab]);

  // Load daily appointments when date changes in admin appointment form
  useEffect(() => {
    if (isAuthenticated && activeTab === 2 && adminAppointmentData.date) {
      loadDailyAppointments(adminAppointmentData.date.format('YYYY-MM-DD'));
    }
  }, [isAuthenticated, activeTab, adminAppointmentData.date]);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      setIsAuthenticated(true);
      // In a real app, you'd validate the token with the server
      setUser({ email: 'admin@example.com' });
    }
    setLoading(false);
  };

  const initializeGoogleSignIn = () => {
    console.log('Initializing Google Sign-In...');
    console.log('Google Client ID:', process.env.REACT_APP_GOOGLE_CLIENT_ID);
    
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      console.error('REACT_APP_GOOGLE_CLIENT_ID is not set!');
      setError('Google OAuth is not configured. Please check environment variables.');
      return;
    }
    
    let retryCount = 0;
    const maxRetries = 50; // 5 seconds max wait
    
    // Wait for Google Identity Services to load
    const checkGoogleLoaded = () => {
      console.log(`Checking Google Identity Services... attempt ${retryCount + 1}`);
      
      if (window.google && window.google.accounts) {
        console.log('Google Identity Services loaded successfully');
        
        const buttonContainer = document.getElementById('google-signin-button');
        if (!buttonContainer) {
          return;
        }
        
        console.log('Button container found:', buttonContainer);
        
        try {
          window.google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            callback: handleGoogleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
          });
          
          console.log('Google OAuth initialized successfully');
          
          // Render the Google Sign-In button
          window.google.accounts.id.renderButton(
            buttonContainer,
            { 
              theme: 'outline', 
              size: 'large',
              text: 'signin_with',
              shape: 'rectangular'
            }
          );
          
          console.log('Google Sign-In button rendered successfully');
          
        } catch (error) {
          console.error('Error initializing Google Sign-In:', error);
          setError(`Failed to initialize Google Sign-In: ${error.message}`);
        }
      } else {
        retryCount++;
        console.log(`Google Identity Services not loaded yet, retry ${retryCount}/${maxRetries}...`);
        
        if (retryCount >= maxRetries) {
          console.error('Google Identity Services failed to load after maximum retries');
          setError('Google Sign-In failed to load. Please check your internet connection and refresh the page.');
          return;
        }
        
        // Retry after a short delay
        setTimeout(checkGoogleLoaded, 100);
      }
    };
    
    checkGoogleLoaded();
  };

  const handleGoogleCredentialResponse = async (response) => {
    try {
      const result = await adminApi.googleLogin(response.credential);
      setIsAuthenticated(true);
      setUser(result.user);
      setError('');
    } catch (err) {
      setError('Authentication failed. Please check your credentials.');
      console.error('Login error:', err);
    }
  };

  const handleLogout = () => {
    adminApi.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const loadAppointments = async () => {
    setAppointmentsLoading(true);
    try {
      const startDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
      const endDate = dayjs().add(3, 'months').format('YYYY-MM-DD');
      const data = await adminApi.getAppointments(startDate, endDate);
      setAppointments(data);
    } catch (err) {
      setError('Failed to load appointments');
      console.error('Error loading appointments:', err);
    } finally {
      setAppointmentsLoading(false);
    }
  };

  const loadAvailabilitySlots = async () => {
    setAvailabilityLoading(true);
    try {
      const startDate = dayjs().format('YYYY-MM-DD');
      const endDate = dayjs().add(3, 'months').format('YYYY-MM-DD');
      const data = await adminApi.getAvailabilitySlots(startDate, endDate);
      setAvailabilitySlots(data);
    } catch (err) {
      setError('Failed to load availability slots');
      console.error('Error loading availability slots:', err);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handleCreateAvailability = async () => {
    try {
      const requestData = {
        date: availabilityData.date.format('YYYY-MM-DD'),
        startTime: availabilityData.startTime.format('HH:mm'),
        endTime: availabilityData.endTime.format('HH:mm'),
        duration: availabilityData.duration,
        isRecurring: availabilityData.isRecurring,
        recurringDays: availabilityData.recurringDays,
        recurringEndDate: availabilityData.isRecurring ? availabilityData.recurringEndDate.format('YYYY-MM-DD') : null
      };

      await adminApi.createAdvancedAvailability(requestData);
      setAvailabilityDialog(false);
      setError('');
      loadAvailabilitySlots(); // Refresh the list
      
      // Reset form
      setAvailabilityData({
        date: dayjs(),
        startTime: dayjs().hour(9).minute(0),
        endTime: dayjs().hour(17).minute(0),
        duration: 60,
        isRecurring: false,
        recurringDays: [],
        recurringEndDate: dayjs().add(3, 'month'),
      });
    } catch (err) {
      setError('Failed to create availability');
      console.error('Error creating availability:', err);
    }
  };

  const handleEditAvailabilitySlot = async () => {
    if (!slotToEdit) return;
    
    try {
      const updateData = {
        slotId: slotToEdit.id,
        newTime: editSlotData.newTime || undefined,
        newEndTime: editSlotData.newEndTime || undefined,
        isAvailable: editSlotData.isAvailable,
        updateRecurringSeries: editSlotData.updateRecurringSeries
      };

      await adminApi.updateAvailabilitySlot(slotToEdit.id, updateData);
      setEditAvailabilityDialog(false);
      setSlotToEdit(null);
      setEditSlotData({
        newTime: '',
        newEndTime: '',
        isAvailable: true,
        updateRecurringSeries: false
      });
      loadAvailabilitySlots(); // Refresh the list
    } catch (err) {
      setError('Failed to update availability slot');
      console.error('Error updating availability slot:', err);
    }
  };

  const handleDeleteAvailabilitySlot = async (slot, deleteRecurringSeries = false) => {
    try {
      await adminApi.deleteAvailabilitySlot(slot.id, deleteRecurringSeries);
      loadAvailabilitySlots(); // Refresh the list
    } catch (err) {
      setError('Failed to delete availability slot');
      console.error('Error deleting availability slot:', err);
    }
  };

  // Load weekly availability for admin scheduling
  const loadWeeklyAvailability = async (weekStart) => {
    setWeeklyAvailabilityLoading(true);
    try {
      const startDate = weekStart.format('YYYY-MM-DD');
      const endDate = weekStart.add(6, 'days').format('YYYY-MM-DD');
      const data = await adminApi.getWeeklyAvailability(startDate, endDate);
      setWeeklyAvailability(data);
    } catch (err) {
      console.error('Error loading weekly availability:', err);
      setError('Failed to load weekly availability');
    } finally {
      setWeeklyAvailabilityLoading(false);
    }
  };

  // Load daily appointments for timeline view
  const loadDailyAppointments = async (date) => {
    setDailyAppointmentsLoading(true);
    try {
      const data = await adminApi.getAppointmentsForDate(date);
      setDailyAppointments(data);
    } catch (err) {
      console.error('Error loading daily appointments:', err);
      // Don't show error for daily appointments as it's not critical
    } finally {
      setDailyAppointmentsLoading(false);
    }
  };

  // Handle opening weekly availability dialog
  const openWeeklyAvailabilityDialog = () => {
    setCurrentWeekStart(dayjs().startOf('week'));
    setWeeklyAvailabilityDialog(true);
    loadWeeklyAvailability(dayjs().startOf('week'));
  };

  // Handle selecting a time slot from weekly availability
  const handleSelectTimeSlot = (date, time) => {
    setAdminAppointmentData(prev => ({
      ...prev,
      date: dayjs(date),
      time: time
    }));
    setWeeklyAvailabilityDialog(false);
  };

  // Handle selecting time from daily timeline
  const handleTimelineClick = (time) => {
    setAdminAppointmentData(prev => ({
      ...prev,
      time: time
    }));
  };

  // Navigate weeks in availability dialog
  const navigateWeek = (direction) => {
    const newWeekStart = direction === 'next' 
      ? currentWeekStart.add(1, 'week')
      : currentWeekStart.subtract(1, 'week');
    setCurrentWeekStart(newWeekStart);
    loadWeeklyAvailability(newWeekStart);
  };

  const handleCreateAdminAppointment = async () => {
    try {
      const appointmentData = {
        date: adminAppointmentData.date.format('YYYY-MM-DD'),
        time: adminAppointmentData.time,
        firstName: adminAppointmentData.firstName,
        lastName: adminAppointmentData.lastName,
        email: adminAppointmentData.email,
        phone: adminAppointmentData.phone,
        message: adminAppointmentData.message,
        discountCode: adminAppointmentData.discountCode,
        appointmentType: t(`schedule.${adminAppointmentData.appointmentType}`)
      };

      await adminApi.createAdminAppointment(appointmentData);
      setAdminAppointmentDialog(false);
      setError('');
      loadAppointments(); // Refresh the list
      
      // Refresh daily appointments for the selected date
      if (adminAppointmentData.date) {
        loadDailyAppointments(adminAppointmentData.date.format('YYYY-MM-DD'));
      }
      
      // Reset form
      setAdminAppointmentData({
        date: dayjs(),
        time: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
        discountCode: '',
        appointmentType: t('schedule.firstAppointment')
      });
    } catch (err) {
      console.error('Error creating admin appointment:', err);
      
      // Handle specific conflict errors
      if (err.message.includes('Another appointment already exists') || 
          err.message.includes('overlaps with existing appointment')) {
        setError(`Appointment conflict: There is already an appointment scheduled at ${adminAppointmentData.time} on ${adminAppointmentData.date.format('DD.MM.YYYY')}. Please choose a different time.`);
      } else {
        setError(err.message || 'Failed to create appointment');
      }
    }
  };

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel) return;
    
    try {
      await adminApi.cancelAppointmentAsAdmin(
        appointmentToCancel.id,
        cancelReason,
        notifyClient
      );
      setCancelDialog(false);
      setAppointmentToCancel(null);
      setCancelReason('');
      setNotifyClient(true);
      loadAppointments(); // Refresh the list
    } catch (err) {
      setError('Failed to cancel appointment');
      console.error('Error canceling appointment:', err);
    }
  };

  const openEditAvailabilityDialog = (slot) => {
    setSlotToEdit(slot);
    setEditSlotData({
      newTime: slot.time,
      newEndTime: slot.endTime,
      isAvailable: slot.isAvailable,
      updateRecurringSeries: false
    });
    setEditAvailabilityDialog(true);
  };

  const openCancelDialog = (appointment) => {
    setAppointmentToCancel(appointment);
    setCancelDialog(true);
  };

  const handleRecurringDayToggle = (dayIndex) => {
    setAvailabilityData(prev => ({
      ...prev,
      recurringDays: prev.recurringDays.includes(dayIndex)
        ? prev.recurringDays.filter(d => d !== dayIndex)
        : [...prev.recurringDays, dayIndex]
    }));
  };

  const getDayName = (dayIndex) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayIndex];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Header />
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
              {t('admin.title')}
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            <Box sx={{ mb: 2 }}>
              <div id="google-signin-button"></div>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              {t('admin.unauthorized')}
            </Typography>
          </Paper>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ flexGrow: 1 }}>
        <Header />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
          <Typography variant="h4">
            {t('admin.title')}
          </Typography>
          <Button onClick={handleLogout} variant="outlined">
            {t('admin.logout')}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ mb: 3 }} square>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label={t('admin.appointments')} />
            <Tab label={t('admin.availability')} />
            <Tab label="Create Appointment" />
          </Tabs>
        </Paper>

        {/* Appointments Tab */}
        {activeTab === 0 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6">
                {t('admin.appointments')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                
                <Button onClick={loadAppointments} disabled={appointmentsLoading}>
                  Refresh
                </Button>
              </Box>
            </Box>

            {appointmentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {appointments.map((appointment) => (
                      <TableRow key={appointment.id}>
                        <TableCell>
                          {dayjs(appointment.date).format('DD.MM.YYYY')}
                        </TableCell>
                        <TableCell>{appointment.time}</TableCell>
                        <TableCell>
                          {appointment.firstName} {appointment.lastName}
                        </TableCell>
                        <TableCell>{appointment.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={appointment.status}
                            color={getStatusColor(appointment.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => openCancelDialog(appointment)}
                            color="error"
                            size="small"
                            disabled={appointment.status === 'cancelled'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* Availability Tab */}
        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6">
                {t('admin.availability')}
              </Typography>
              <Button
                variant="contained"
                onClick={() => setAvailabilityDialog(true)}
              >
                {t('admin.createSlot')}
              </Button>
            </Box>

            {availabilityLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Start Time</TableCell>
                      <TableCell>End Time</TableCell>
                      <TableCell>Available</TableCell>
                      <TableCell>Recurring</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availabilitySlots.map((slot) => (
                      <TableRow key={slot.id}>
                        <TableCell>
                          {dayjs(slot.date).format('DD.MM.YYYY')}
                        </TableCell>
                        <TableCell>{slot.time}</TableCell>
                        <TableCell>{slot.endTime}</TableCell>
                        <TableCell>
                          <Chip
                            label={slot.isAvailable ? 'Available' : 'Unavailable'}
                            color={slot.isAvailable ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {slot.isRecurring ? (
                            <Chip label="Recurring" color="info" size="small" />
                          ) : (
                            <Chip label="One-time" color="default" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              onClick={() => openEditAvailabilityDialog(slot)}
                              color="primary"
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteAvailabilitySlot(slot, false)}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                            {slot.isRecurring && (
                              <Button
                                onClick={() => handleDeleteAvailabilitySlot(slot, true)}
                                color="error"
                                size="small"
                                variant="outlined"
                              >
                                Delete Series
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* Create Appointment Tab */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Create Admin Appointment
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create appointments directly without availability restrictions. Appointments will be pre-confirmed and a confirmation email will be sent to the client.
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ position: 'relative' }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={openWeeklyAvailabilityDialog}
                    sx={{ 
                      position: 'absolute', 
                      top: -35, 
                      right: -30, 
                      zIndex: 1,
                      fontSize: '0.75rem'
                    }}
                  >
                    📅 View Weekly Availability
                  </Button>
                  <DatePicker
                    label="Date"
                    value={adminAppointmentData.date}
                    onChange={(date) => setAdminAppointmentData(prev => ({ ...prev, date }))}
                    minDate={dayjs()}
                    sx={{ width: '100%' }}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Time"
                  type="time"
                  value={adminAppointmentData.time}
                  onChange={(e) => setAdminAppointmentData(prev => ({ ...prev, time: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Daily Appointments Timeline */}
              {adminAppointmentData.date && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Existing Appointments on {adminAppointmentData.date.format('DD.MM.YYYY')}:
                  </Typography>
                  {dailyAppointmentsLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">Loading appointments...</Typography>
                    </Box>
                  ) : dailyAppointments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No appointments scheduled for this date
                    </Typography>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      flexWrap: 'wrap',
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.paper'
                    }}>
                      {dailyAppointments.map((appointment, index) => {
                        const startTime = appointment.time;
                        const endTime = appointment.time ? 
                          dayjs(`1970-01-01T${appointment.time}:00`)
                            .add(90, 'minutes')
                            .format('HH:mm') : '';
                        
                        return (
                          <Chip
                            key={appointment.id || index}
                            label={`${startTime} - ${endTime}`}
                            variant="outlined"
                            color="secondary"
                            size="small"
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                            onClick={() => handleTimelineClick(startTime)}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="appointment-type-label">Appointment Type</InputLabel>
                  <Select
                    labelId="appointment-type-label"
                    id="appointment-type-select"
                    value={adminAppointmentData.appointmentType}
                    onChange={(e) => setAdminAppointmentData(prev => ({ ...prev, appointmentType: e.target.value }))}
                  >
                    <MenuItem value="firstAppointment">{t('schedule.firstAppointment')}</MenuItem>
                    <MenuItem value="followUpAppointment">{t('schedule.followUpAppointment')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={adminAppointmentData.firstName}
                  onChange={(e) => setAdminAppointmentData(prev => ({ ...prev, firstName: e.target.value }))}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={adminAppointmentData.lastName}
                  onChange={(e) => setAdminAppointmentData(prev => ({ ...prev, lastName: e.target.value }))}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={adminAppointmentData.email}
                  onChange={(e) => setAdminAppointmentData(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={adminAppointmentData.phone}
                  onChange={(e) => setAdminAppointmentData(prev => ({ ...prev, phone: e.target.value }))}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message/Notes"
                  multiline
                  rows={3}
                  value={adminAppointmentData.message}
                  onChange={(e) => setAdminAppointmentData(prev => ({ ...prev, message: e.target.value }))}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Discount Code"
                  value={adminAppointmentData.discountCode}
                  onChange={(e) => setAdminAppointmentData(prev => ({ ...prev, discountCode: e.target.value }))}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleCreateAdminAppointment}
                disabled={!adminAppointmentData.firstName || !adminAppointmentData.lastName || !adminAppointmentData.email || !adminAppointmentData.time}
              >
                Create Appointment
              </Button>
            </Box>
          </Paper>
        )}

        {/* Create Availability Dialog */}
        <Dialog open={availabilityDialog} onClose={() => setAvailabilityDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>{t('admin.createSlot')}</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Date"
                  value={availabilityData.date}
                  onChange={(date) => setAvailabilityData(prev => ({ ...prev, date }))}
                  minDate={dayjs()}
                  sx={{ width: '100%' }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (minutes)"
                  type="number"
                  value={availabilityData.duration}
                  onChange={(e) => setAvailabilityData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                  inputProps={{ min: 120, max: 240, step: 15 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TimePicker
                  label="Start Time"
                  value={availabilityData.startTime}
                  onChange={(time) => setAvailabilityData(prev => ({ ...prev, startTime: time }))}
                  sx={{ width: '100%' }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TimePicker
                  label="End Time"
                  value={availabilityData.endTime}
                  onChange={(time) => setAvailabilityData(prev => ({ ...prev, endTime: time }))}
                  sx={{ width: '100%' }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={availabilityData.isRecurring}
                      onChange={(e) => setAvailabilityData(prev => ({ 
                        ...prev, 
                        isRecurring: e.target.checked,
                        recurringDays: e.target.checked ? prev.recurringDays : []
                      }))}
                    />
                  }
                  label="Recurring Weekly"
                />
              </Grid>
              
              {availabilityData.isRecurring && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Recurring Days:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => (
                        <Chip
                          key={dayIndex}
                          label={getDayName(dayIndex)}
                          clickable
                          color={availabilityData.recurringDays.includes(dayIndex) ? 'primary' : 'default'}
                          onClick={() => handleRecurringDayToggle(dayIndex)}
                        />
                      ))}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <DatePicker
                      label="Recurring End Date"
                      value={availabilityData.recurringEndDate}
                      onChange={(date) => setAvailabilityData(prev => ({ ...prev, recurringEndDate: date }))}
                      minDate={availabilityData.date.add(1, 'week')}
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAvailabilityDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleCreateAvailability} 
              variant="contained"
              disabled={!availabilityData.date || !availabilityData.startTime || !availabilityData.endTime || 
                (availabilityData.isRecurring && availabilityData.recurringDays.length === 0)}
            >
              {t('common.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Availability Dialog */}
        <Dialog open={editAvailabilityDialog} onClose={() => setEditAvailabilityDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Availability Slot</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Time"
                  type="time"
                  value={editSlotData.newTime}
                  onChange={(e) => setEditSlotData(prev => ({ ...prev, newTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={editSlotData.newEndTime}
                  onChange={(e) => setEditSlotData(prev => ({ ...prev, newEndTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editSlotData.isAvailable}
                      onChange={(e) => setEditSlotData(prev => ({ ...prev, isAvailable: e.target.checked }))}
                    />
                  }
                  label="Available"
                />
              </Grid>
              
              {slotToEdit?.isRecurring && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editSlotData.updateRecurringSeries}
                        onChange={(e) => setEditSlotData(prev => ({ ...prev, updateRecurringSeries: e.target.checked }))}
                      />
                    }
                    label="Update entire recurring series"
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditAvailabilityDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditAvailabilitySlot} variant="contained">
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Appointment Dialog */}
        <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('admin.cancelAppointment')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={t('admin.reason')}
              multiline
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              sx={{ mt: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyClient}
                  onChange={(e) => setNotifyClient(e.target.checked)}
                />
              }
              label={t('admin.notifyClient')}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCancelAppointment} variant="contained" color="error">
              {t('admin.cancelAppointment')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Weekly Availability Dialog */}
        <Dialog 
          open={weeklyAvailabilityDialog} 
          onClose={() => setWeeklyAvailabilityDialog(false)} 
          maxWidth="lg" 
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button onClick={() => navigateWeek('prev')} size="medium">←</Button>
                <Typography variant="subtitle1" style={{ fontSize: '1rem' }}>
                  {currentWeekStart.format('DD.MM.YYYY')} - {currentWeekStart.add(6, 'days').format('DD.MM.YYYY')}
                </Typography>
                <Button onClick={() => navigateWeek('next')} size="medium">→</Button>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {weeklyAvailabilityLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {Array.from({ length: 7 }, (_, dayIndex) => {
                  const date = currentWeekStart.add(dayIndex, 'days');
                  const dateString = date.format('YYYY-MM-DD');
                  const dayName = date.format('dddd');
                  const timeSlots = weeklyAvailability[dateString] || [];
                  if(timeSlots.length === 0) {
                    return null;
                  }
                  return (
                    <Grid item xs={12} md={6} lg={4} key={dayIndex}>
                      <Paper sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                          {dayName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                          {date.format('DD.MM.YYYY')}
                        </Typography>
                        
                        {timeSlots.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            No availability
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 300, overflow: 'auto' }}>
                            {timeSlots.map((timeSlot, index) => (
                              <Button
                                key={index}
                                variant="outlined"
                                size="small"
                                onClick={() => handleSelectTimeSlot(dateString, timeSlot)}
                                sx={{ 
                                  justifyContent: 'center',
                                  minHeight: 32,
                                  fontSize: '0.75rem'
                                }}
                              >
                                {timeSlot}
                              </Button>
                            ))}
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWeeklyAvailabilityDialog(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      <Footer />
    </Box>
  );
};

export default Admin; 